import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Empty } from '@creditornot/cb-placeholders';
import React, { ReactNode, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { RouteProps, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import BulkImageUploadPage from 'modules/bulk-image-upload/components/BulkImageUploadPage';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import { useTrackImportImageUpload, useTrackImportItemImagesFileSelected } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import BulkImageConfirmationPrompt from './components/BulkImageConfirmationPrompt';
import FileMatcherConfirmationPrompt from './components/FileMatcherConfirmationPrompt';
import NavigationBar from './components/NavigationBar';
import useBulkImageUpload from './hooks/useBulkImageUpload';
import useFileMatcher from './hooks/useFileMatcher';

type RouteRenderProp = RouteProps['render'];

type Props = {
  venue: Venue;
  children: ({ routeRenderProp }: { routeRenderProp: RouteRenderProp }) => ReactNode;
};

const NoMenuPage = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledEmpty = styled(Empty)`
  margin-block-start: 32px;
`;

const BulkImageRouteRenderer = ({ venue, children }: Props) => {
  const history = useHistory();
  const intl = useIntl();
  const { menu, items, loadingMenuData, menuDataError, refetchMenuData } = useMenuData(
    !!history.location.pathname.includes('/bulk-image-upload'),
  );

  const menuLocaleContext = useMemo(
    () => ({
      country: venue.country,
      primaryLanguage: menu?.primary_language || ('en' as const),
      timezone: venue.timezone,
    }),
    [menu?.primary_language, venue.country, venue.timezone],
  );

  const { handleFileMatching, results, matchBy, setMatchBy, isMatching, setErrors, resetMatcherState } =
    useFileMatcher(items);
  const { handleBulkImageUpload, uploadStatus, resetUploadState } = useBulkImageUpload(results || [], venue, setErrors);
  const isUploading = uploadStatus === 'loading';
  const isPending = !!results?.filter(r => r.status === 'success').length && uploadStatus === 'idle';

  useTrackImportImageUpload(uploadStatus);
  useTrackImportItemImagesFileSelected(results ?? []);

  const resetBulkUploadState = useCallback(() => {
    toast.dismiss();
    resetMatcherState();
    resetUploadState();
    if (uploadStatus === 'success') {
      refetchMenuData();
    }
  }, [refetchMenuData, resetMatcherState, resetUploadState, uploadStatus]);

  const uploadPageState = useMemo(() => {
    if (isMatching) {
      return 'matching';
    }
    if (loadingMenuData) {
      return 'reading-menu';
    }
    return uploadStatus;
  }, [isMatching, loadingMenuData, uploadStatus]);

  const routeRenderProp = useMemo<RouteRenderProp>(
    () => () => (
      <BulkImageUploadPage
        results={results}
        state={uploadPageState}
        menuError={menuDataError}
        matchBy={matchBy}
        onMatchBy={setMatchBy}
        onFileMatching={handleFileMatching}
        onFileUpload={handleBulkImageUpload}
        onBackButtonClick={() => {
          if (!isUploading && !isPending) {
            resetBulkUploadState();
          }
          history.goBack();
        }}
      />
    ),
    [
      handleBulkImageUpload,
      handleFileMatching,
      history,
      isPending,
      isUploading,
      matchBy,
      menuDataError,
      resetBulkUploadState,
      results,
      setMatchBy,
      uploadPageState,
    ],
  );

  const noMenuRenderProp: RouteRenderProp = () => (
    <NoMenuPage>
      <NavigationBar onBackButtonClick={() => history.goBack()} />
      <StyledEmpty title={intl.formatMessage({ id: 'menu.noMenuForVenue' })} />
    </NoMenuPage>
  );

  return venue.menu ? (
    <MenuLocaleContext.Provider value={menuLocaleContext}>
      {children({ routeRenderProp })}
      <FileMatcherConfirmationPrompt when={isPending} onConfirm={resetBulkUploadState} />
      <BulkImageConfirmationPrompt when={isUploading} venueSlug={venue.slug} />
    </MenuLocaleContext.Provider>
  ) : (
    <>{children({ routeRenderProp: noMenuRenderProp })}</>
  );
};

export default React.memo(BulkImageRouteRenderer);
